import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllHarvests } from '../../services/Harvest/harvestService';
import CreateHarvestModal from './CreateHarvestModal';
import EditHarvestModal from './EditHarvestModal';
import HarvestFilter from '../Filters/HarvestFilter';
import { formatDate } from '../Utils/utils';
import { FaCheckCircle, FaTimesCircle, FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Harvest = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setHarvests] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateHarvestModal, setShowCreateHarvestModal] = useState(false);
  const [showEditHarvestModal, setShowEditHarvestModal] = useState(false);
  const [selectedHarvest, setSelectedHarvest] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});

  const canView = useMemo(() => hasPermission('harvestView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('harvestCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('harvestUpdate'), [hasPermission, permissions]);

  const fetchHarvests = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllHarvests(filters, currentPage - 1, currentPageSize);
      setHarvests(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchHarvests();
    }
  }, [page, pageSize, fetchHarvests, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateHarvestModal = () => {
    if (!checkPermission('harvestCreate')) {
      return;
    }
    setShowCreateHarvestModal(true);
  };
  const handleCloseCreateHarvestModal = () => setShowCreateHarvestModal(false);

  const handleOpenEditHarvestModal = (harvest) => {
    if (!checkPermission('harvestUpdate')) {
      return;
    }
    setSelectedHarvest(harvest);
    setShowEditHarvestModal(true);
  };
  
  const handleCloseEditHarvestModal = () => {
    setSelectedHarvest(null);
    setShowEditHarvestModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchHarvests(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setFilterParams({});
    setPage(1);
    fetchHarvests({}, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Safras</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Safras</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateHarvestModal}>
              Cadastrar Safra
            </Button>
          )}
        </Col>
      </Row>
      {canView && <HarvestFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Início</th>
              <th>Fim</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ativo</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((harvest) => (
              <tr key={harvest.id}>
                <td>{harvest.description}</td>
                <td>{formatDate(harvest.startDate)}</td>
                <td>{formatDate(harvest.endDate)}</td>
                <td style={{textAlign: 'center'}}>
                  {harvest.active ? (
                    <FaCheckCircle style={{ color: 'green' }} />
                  ) : (
                    <FaTimesCircle style={{ color: 'red' }} />
                  )}
                </td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditHarvestModal(harvest)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateHarvestModal
        show={showCreateHarvestModal}
        handleClose={handleCloseCreateHarvestModal}
        refreshHarvests={fetchHarvests}
      />

      <EditHarvestModal
        show={showEditHarvestModal}
        handleClose={handleCloseEditHarvestModal}
        harvest={selectedHarvest}
        refreshHarvests={fetchHarvests}
      />
    </Container>
  );
};

export default Harvest;